/**
 * ログインページコンポーネント
 *
 * Googleログインを提供
 * 拡張機能からのログインにも対応
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

function LoginPage() {
  const { loginWithGoogle, error, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [extensionLoginComplete, setExtensionLoginComplete] = useState(false);

  // 拡張機能からのログインかどうか
  const isFromExtension = searchParams.get('from') === 'extension';

  /**
   * ログイン完了時に拡張機能に通知
   */
  useEffect(() => {
    if (user && isFromExtension && !extensionLoginComplete) {
      console.log('[KnoVelo] 拡張機能にログイン完了を通知');

      // ユーザー情報を準備
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL
      };

      // 拡張機能にメッセージを送信（chrome.runtime APIを使用）
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        // 拡張機能IDが必要な場合は外部メッセージを使用
        // ここではWebアプリからは直接送れないため、
        // postMessageを使ってwindow間で通信
      }

      // LocalStorageにも保存（拡張機能が読み取れるように）
      try {
        localStorage.setItem('knovelo_auth_user', JSON.stringify(userData));
      } catch (e) {
        console.error('LocalStorage保存エラー:', e);
      }

      setExtensionLoginComplete(true);

      // 拡張機能ユーザーには完了メッセージを表示
      // 自動でウィンドウを閉じる（ポップアップとして開かれた場合）
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  }, [user, isFromExtension, extensionLoginComplete]);

  /**
   * Googleログインを実行
   */
  async function handleGoogleLogin() {
    setIsLoading(true);
    try {
      await loginWithGoogle();
      // 成功時は自動的にダッシュボードへリダイレクト（通常のWebログインの場合）
      // 拡張機能からの場合はuseEffectで処理
    } catch (err) {
      console.error('ログインエラー:', err);
    } finally {
      setIsLoading(false);
    }
  }

  // 拡張機能からのログイン完了画面
  if (user && isFromExtension) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-md">
          <img
            src="/knovelo-trans-icon.png"
            alt="KnoVelo"
            className="w-20 h-20 mx-auto mb-4"
          />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            ログイン完了！
          </h2>
          <p className="text-gray-600 mb-4">
            {user.displayName || user.email}さん、ようこそ！
          </p>
          <p className="text-sm text-gray-500">
            このウィンドウは自動的に閉じます...<br />
            閉じない場合は手動で閉じてください。
          </p>
          <button
            onClick={() => window.close()}
            className="mt-4 btn-primary"
          >
            ウィンドウを閉じる
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* ログインカード */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* ロゴ・タイトル */}
          <div className="text-center mb-8">
            <img
              src="/knovelo-trans-icon.png"
              alt="KnoVelo"
              className="w-24 h-24 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              KnoVelo
            </h1>
            <p className="text-gray-500">
              記憶のガイド
            </p>
          </div>

          {/* キャッチコピー */}
          <div className="bg-gray-50 rounded-2xl p-4 mb-8">
            <p className="text-center text-gray-600 text-sm">
              「昨日見た赤い橋のページ」<br />
              そんな曖昧な記憶でも、<br />
              <span className="text-primary font-medium">Thoth</span>があなたの情報を見つけます
            </p>
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div className="bg-red-50 text-red-600 rounded-xl p-4 mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Googleログインボタン */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-200 rounded-xl font-semibold text-gray-700 hover:border-primary hover:text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="spinner" />
            ) : (
              <>
                {/* Google アイコン */}
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Googleでログイン
              </>
            )}
          </button>

          {/* 利用規約 */}
          <p className="text-center text-xs text-gray-400 mt-6">
            ログインすることで、
            <a href="#" className="text-primary hover:underline">利用規約</a>
            と
            <a href="#" className="text-primary hover:underline">プライバシーポリシー</a>
            に同意したものとみなされます
          </p>
        </div>

        {/* 特徴 */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="p-4">
            <div className="text-2xl mb-2">🔍</div>
            <p className="text-xs text-gray-600">曖昧検索</p>
          </div>
          <div className="p-4">
            <div className="text-2xl mb-2">🤖</div>
            <p className="text-xs text-gray-600">AI要約</p>
          </div>
          <div className="p-4">
            <div className="text-2xl mb-2">🏷️</div>
            <p className="text-xs text-gray-600">自動タグ</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
